import { Navigate, Route, Routes } from "react-router-dom";
import { ApplicationWorkspaceLayout } from "./application/ApplicationWorkspaceLayout";
import { BeneficiaryTab } from "./application/tabs/BeneficiaryTab";
import { BankingTab } from "./application/tabs/BankingTab";
import { BillsTab } from "./application/tabs/BillsTab";
import { EngineerTab } from "./application/tabs/EngineerTab";
import { MilestonesTab } from "./application/tabs/MilestonesTab";
import { OverviewTab } from "./application/tabs/OverviewTab";
import { SubcontractorTab } from "./application/tabs/SubcontractorTab";
import { ValidationTab } from "./application/tabs/ValidationTab";
import { WorkProgressTab } from "./application/tabs/WorkProgressTab";
import { PoRegisterTab } from "./application/tabs/PoRegisterTab";
import { AuthenticatedShell } from "./components/AuthenticatedShell";
import { Layout } from "./components/Layout";
import { RequireAuth } from "./components/RequireAuth";
import { RequirePagePermission } from "./components/RequirePagePermission";
import { Applications } from "./pages/Applications";
import { ConsoleUsersPage } from "./pages/ConsoleUsersPage";
import { AuditLogPage } from "./pages/AuditLogPage";
import { SubcontractorOnboarding } from "./pages/SubcontractorOnboarding";
import { SubcontractorBeneficiaryDedicated } from "./pages/SubcontractorBeneficiaryDedicated";
import { SubcontractorToBeneficiary } from "./pages/SubcontractorToBeneficiary";
import { EngineerOnboarding } from "./pages/EngineerOnboarding";
import { MilestonesPage } from "./pages/MilestonesPage";
import { MilestoneMaster } from "./pages/MilestoneMaster";
import { GeoDirectoryMasterPage } from "./pages/GeoDirectoryMasterPage";
import { GeographicReports } from "./pages/GeographicReports";
import { ReportsHubPage } from "./pages/ReportsHubPage";
import { LeadsListPage } from "./pages/LeadsListPage";
import { BillsOutwardPage } from "./pages/HubPlaceholder";
import { InvoiceRegisterPage } from "./pages/InvoiceRegisterPage";
import { InvoiceFormatSettingsPage } from "./pages/InvoiceFormatSettingsPage";
import { LabourSupplierOnboardingPage } from "./pages/LabourSupplierOnboardingPage";
import { InventoryOutwardPage } from "./pages/InventoryOutwardPage";
import { GoodsReceiptsPage } from "./pages/GoodsReceiptsPage";
import { InventoryInwardPage } from "./pages/InventoryInwardPage";
import { StoreAuditPage } from "./pages/StoreAuditPage";
import { StoreStocksPage } from "./pages/StoreStocksPage";
import { StoresYardsPage } from "./pages/StoresYardsPage";
import { AgentMasterPage } from "./pages/AgentMasterPage";
import { ConstructionTimelineMaster } from "./pages/ConstructionTimelineMaster";
import { IssueMaterialRegisterPage } from "./pages/IssueMaterialRegisterPage";
import { PoFormatSettingsPage } from "./pages/PoFormatSettingsPage";
import { StandardResourcesMaster } from "./pages/StandardResourcesMaster";
import { ZonesPage } from "./pages/ZonesPage";
import { GeoTreePage } from "./pages/GeoTreePage";
import { HomeEntry } from "./pages/HomeEntry";
import { LoginPage } from "./pages/LoginPage";
import { SubcontractorPortalPage } from "./pages/SubcontractorPortalPage"; // Force reload
import { TimelineApprovalsPage } from "./pages/TimelineApprovalsPage";
import { ProjectInitiationPage } from "./pages/ProjectInitiationPage";
import { APPLICATIONS_HUB_ROUTE_PERMISSIONS } from "./config/applicationHubPermissions";

function P({
  permission,
  requireAll,
  alsoRequireAll,
  children,
}: {
  permission: string | string[];
  requireAll?: boolean;
  alsoRequireAll?: string[];
  children: React.ReactNode;
}) {
  return (
    <RequirePagePermission permission={permission} requireAll={requireAll} alsoRequireAll={alsoRequireAll}>
      {children}
    </RequirePagePermission>
  );
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/subcontractor-portal/:token" element={<SubcontractorPortalPage />} />
      <Route element={<RequireAuth />}>
        <Route element={<AuthenticatedShell />}>
          <Route path="/" element={<Layout />}>
            <Route index element={<HomeEntry />} />
            <Route
              path="reports"
              element={
                <P permission="reports">
                  <ReportsHubPage />
                </P>
              }
            />
            <Route
              path="reports/area"
              element={
                <P permission="reports">
                  <GeographicReports />
                </P>
              }
            />
            <Route
              path="geo-tree"
              element={
                <P permission={["geo_tree", "reports"]}>
                  <GeoTreePage />
                </P>
              }
            />
            <Route path="leads/import" element={<Navigate to="/leads?import=1" replace />} />
            <Route
              path="leads"
              element={
                <P permission={["leads", "leads_import"]}>
                  <LeadsListPage />
                </P>
              }
            />
            <Route
              path="applications"
              element={
                <P permission={APPLICATIONS_HUB_ROUTE_PERMISSIONS}>
                  <Applications />
                </P>
              }
            />
            <Route
              path="timeline-approvals"
              element={
                <P permission={APPLICATIONS_HUB_ROUTE_PERMISSIONS}>
                  <TimelineApprovalsPage />
                </P>
              }
            />
            <Route
              path="project-initiation"
              element={
                <P permission={APPLICATIONS_HUB_ROUTE_PERMISSIONS}>
                  <ProjectInitiationPage />
                </P>
              }
            />
            <Route
              path="milestone-master"
              element={
                <P permission="milestone_templates">
                  <MilestoneMaster />
                </P>
              }
            />
            <Route
              path="construction-timeline-master"
              element={
                <P permission="construction_timeline">
                  <ConstructionTimelineMaster />
                </P>
              }
            />
            <Route
              path="standard-resources-master"
              element={
                <P permission="standard_resources">
                  <StandardResourcesMaster />
                </P>
              }
            />
            <Route
              path="zones"
              element={
                <P permission="geo_zones">
                  <ZonesPage />
                </P>
              }
            />
            <Route
              path="geo-directory"
              element={
                <P permission={["geo_directory", "geo_directory_admin"]}>
                  <GeoDirectoryMasterPage />
                </P>
              }
            />
            <Route
              path="milestones"
              element={
                <P permission={["applications", "milestone_templates"]} requireAll>
                  <MilestonesPage />
                </P>
              }
            />
            <Route
              path="subcontractor-onboarding"
              element={
                <P permission="subcontractor_masters">
                  <SubcontractorOnboarding />
                </P>
              }
            />
            <Route
              path="engineer-onboarding"
              element={
                <P permission="engineer_masters">
                  <EngineerOnboarding />
                </P>
              }
            />
            <Route
              path="agent-master"
              element={
                <P permission="agents">
                  <AgentMasterPage />
                </P>
              }
            />
            <Route
              path="subcontractor-to-beneficiary"
              element={
                <P permission={APPLICATIONS_HUB_ROUTE_PERMISSIONS}>
                  <SubcontractorToBeneficiary />
                </P>
              }
            />
            <Route
              path="engineer-to-beneficiary"
              element={
                <P permission={APPLICATIONS_HUB_ROUTE_PERMISSIONS}>
                  <Navigate to="/subcontractor-to-beneficiary" replace />
                </P>
              }
            />
            <Route
              path="inventory/po-register"
              element={
                <P permission="inventory_issue_po">
                  <IssueMaterialRegisterPage section="po" />
                </P>
              }
            />
            <Route
              path="inventory/po-format"
              element={
                <P permission="inventory_issue_po">
                  <PoFormatSettingsPage />
                </P>
              }
            />
            <Route
              path="inventory/issue-material"
              element={
                <P permission="inventory_issue_material">
                  <IssueMaterialRegisterPage section="issue" />
                </P>
              }
            />
            <Route
              path="inventory/inward"
              element={
                <P permission="inventory_inward">
                  <InventoryInwardPage />
                </P>
              }
            />
            <Route
              path="inventory/goods-receipts"
              element={
                <P permission="inventory_goods_receipts">
                  <GoodsReceiptsPage />
                </P>
              }
            />
            <Route
              path="inventory/store-stocks"
              element={
                <P permission="inventory_store_stocks">
                  <StoreStocksPage />
                </P>
              }
            />
            <Route
              path="inventory/store-audit"
              element={
                <P permission="inventory_store_stocks">
                  <StoreAuditPage />
                </P>
              }
            />
            <Route
              path="inventory/outward"
              element={
                <P permission={["inventory_outward", "inventory_issue_material"]} requireAll>
                  <InventoryOutwardPage />
                </P>
              }
            />
            <Route
              path="inventory/stores-yards"
              element={
                <P permission="inventory_stores_yards">
                  <StoresYardsPage />
                </P>
              }
            />
            <Route
              path="bills/inward"
              element={
                <P permission="bills_inward">
                  <InvoiceRegisterPage />
                </P>
              }
            />
            <Route
              path="bills/inward/format"
              element={
                <P permission="bills_inward">
                  <InvoiceFormatSettingsPage />
                </P>
              }
            />
            <Route
              path="bills/labour-suppliers"
              element={
                <P permission="bills_inward">
                  <LabourSupplierOnboardingPage />
                </P>
              }
            />
            <Route
              path="bills/outward"
              element={
                <P permission="bills_outward">
                  <BillsOutwardPage />
                </P>
              }
            />
            <Route
              path="subcontractor-to-beneficiary/:id"
              element={
                <P permission={APPLICATIONS_HUB_ROUTE_PERMISSIONS}>
                  <SubcontractorBeneficiaryDedicated />
                </P>
              }
            />
            <Route
              path="console-users"
              element={
                <P permission="console_users">
                  <ConsoleUsersPage />
                </P>
              }
            />
            <Route
              path="audit-log"
              element={
                <P permission="audit_log">
                  <AuditLogPage />
                </P>
              }
            />
            <Route
              path="applications/:id"
              element={
                <P permission={APPLICATIONS_HUB_ROUTE_PERMISSIONS}>
                  <ApplicationWorkspaceLayout />
                </P>
              }
            >
              <Route index element={<Navigate to="overview" replace />} />
              <Route
                path="overview"
                element={
                  <P permission={["applications", "dossier_tab_overview"]}>
                    <OverviewTab />
                  </P>
                }
              />
              <Route
                path="beneficiary"
                element={
                  <P permission={["applications", "dossier_tab_beneficiary"]}>
                    <BeneficiaryTab />
                  </P>
                }
              />
              <Route
                path="validation"
                element={
                  <P permission={["applications", "dossier_tab_validation"]}>
                    <ValidationTab />
                  </P>
                }
              />
              <Route
                path="banking"
                element={
                  <P permission={["applications", "dossier_tab_banking"]}>
                    <BankingTab />
                  </P>
                }
              />
              <Route
                path="subcontractor"
                element={
                  <P permission={["applications", "dossier_tab_subcontractor"]}>
                    <SubcontractorTab />
                  </P>
                }
              />
              <Route
                path="engineer"
                element={
                  <P permission={["applications", "dossier_tab_engineer"]}>
                    <EngineerTab />
                  </P>
                }
              />
              <Route
                path="milestones"
                element={
                  <P permission={["applications", "dossier_tab_milestones"]} alsoRequireAll={["milestone_templates"]}>
                    <MilestonesTab />
                  </P>
                }
              />
              <Route
                path="bills"
                element={
                  <P permission={["applications", "dossier_tab_bills"]}>
                    <BillsTab />
                  </P>
                }
              />
              <Route
                path="work-progress"
                element={
                  <P permission={["applications", "dossier_tab_work_progress"]}>
                    <WorkProgressTab />
                  </P>
                }
              />
              <Route
                path="po-register"
                element={
                  <P permission={["applications", "dossier_tab_po_register"]}>
                    <PoRegisterTab />
                  </P>
                }
              />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Route>
      </Route>
    </Routes>
  );
}
